/**
 * WAV Surgeon — pure-JS, streaming-Blob WAV repair.
 *
 * Targets the broken-WAV class produced by handheld recorders (Zoom H1n/H4n/H6,
 * Tascam DR-05/40, RØDE Wireless GO dump) when power cuts mid-write or the SD
 * card is pulled. FFmpeg's WAV demuxer can refuse such files — surgeon parses
 * the RIFF tree manually and rewrites a clean header, then concats the audio
 * payload via Blob.slice (no full-file load, works on 1+ GB recordings).
 *
 * Since 2026-09-04 it also REBUILDS a header that is not there at all:
 * `buildFromRaw(file, layout)` wraps headerless little-endian PCM (the layout
 * comes from PcmLayoutGuess, or from the user) in a clean WAV header — the
 * payload is never copied. Big-endian payloads decline (`big_endian`): WAV
 * cannot carry them and Chromium's Web Audio does not decode AIFF, so the
 * caller hands those to FFmpeg's raw demuxer (`-f s16be …`), which swaps
 * bytes while streaming. And `examine(file, {offset})` walks a
 * RIFF tree that starts at any byte, for files with a junk prefix that
 * MediaLocate has already positioned.
 *
 * Out of scope:
 * - RF64 (>4 GB WAVs) — recognised, declined, FFmpeg fallback handles.
 * - Damaged fmt chunks (channels=0, sampleRate=0) — we cannot guess sane values.
 * - Damaged audio frames inside the data chunk — surgeon repairs HEADERS only.
 *   For corrupt frames, FFmpeg's `-err_detect ignore_err` is the right tool.
 *
 * API:
 *   await WavSurgeon.isWav(file, offset?)         → boolean
 *   await WavSurgeon.examine(file, {offset?})     → diagnosis object
 *   await WavSurgeon.repair(file, opts?)          → { ok, blob, fixes, diagnosis }
 *   WavSurgeon.buildFromRaw(file, layout)         → { ok, blob, ext:'wav', fmtOut, fixes } | { ok:false, reason }
 */
(function(){
'use strict';

if (window.WavSurgeon && !window.WavSurgeon.__isStub) return;

// Cap on how far we scan the head to find fmt + data chunks. ZOOM/Tascam
// recorders write a JUNK pre-pad of up to 4 KB then 'fmt ' + 'data' immediately.
// Pro-Tools and broadcast-WAV (BWF) put a 'bext' metadata block first that can
// run a few KB. 256 KB is paranoia-bound — we've never needed more than 8 KB.
var HEAD_SCAN_LIMIT = 262144;

// Recognised WAV master ids. RF64 is signalled and declined by examine.
var MASTERS = { RIFF: 1, RIFX: 1, RF64: 1, BW64: 1 };

function fourCC(view, off){
    return String.fromCharCode(
        view.getUint8(off), view.getUint8(off+1),
        view.getUint8(off+2), view.getUint8(off+3)
    );
}

function isPrintable4CC(s){
    if (!s || s.length !== 4) return false;
    for (var i = 0; i < 4; i++){
        var c = s.charCodeAt(i);
        // Strict subset that real WAV chunks use — letters, digits, space.
        // Stops chunk-walker on garbage early instead of seeking into noise.
        if (!((c >= 0x30 && c <= 0x39) || (c >= 0x41 && c <= 0x5a) ||
              (c >= 0x61 && c <= 0x7a) || c === 0x20)) return false;
    }
    return true;
}

async function readHead(file, bytes, offset){
    offset = offset || 0;
    var n = Math.min(bytes, file.size - offset);
    return await file.slice(offset, offset + n).arrayBuffer();
}

async function isWav(file, offset){
    offset = offset || 0;
    if (!file || file.size - offset < 12) return false;
    try {
        var ab = await readHead(file, 12, offset);
        var v = new DataView(ab);
        return MASTERS[fourCC(v, 0)] === 1 && fourCC(v, 8) === 'WAVE';
    } catch(_) { return false; }
}

function parseFmt(v, off, size){
    if (size < 16) return { fatal: 'fmt_too_small', size: size };
    var formatTag = v.getUint16(off, true);
    var channels = v.getUint16(off + 2, true);
    var sampleRate = v.getUint32(off + 4, true);
    var avgBytes = v.getUint32(off + 8, true);
    var blockAlign = v.getUint16(off + 12, true);
    var bitsPerSample = v.getUint16(off + 14, true);
    var subFormat = null, validBits = bitsPerSample, channelMask = 0;
    var extensibleBytes = null;
    if (formatTag === 0xFFFE && size >= 40){
        validBits = v.getUint16(off + 18, true);
        channelMask = v.getUint32(off + 20, true);
        // First 2 bytes of the SubFormat GUID are the actual format code.
        // GUID for PCM = {00000001-0000-0010-8000-00aa00389b71},
        // for IEEE_FLOAT = {00000003-0000-0010-8000-00aa00389b71}.
        subFormat = v.getUint16(off + 24, true);
        extensibleBytes = new Uint8Array(v.buffer, v.byteOffset + off + 16, 24);
    }
    return {
        formatTag: formatTag,
        channels: channels,
        sampleRate: sampleRate,
        avgBytesPerSec: avgBytes,
        blockAlign: blockAlign,
        bitsPerSample: bitsPerSample,
        validBitsPerSample: validBits,
        channelMask: channelMask,
        subFormat: subFormat,
        extensibleBytes: extensibleBytes,
        size: size
    };
}

/**
 * @param {File|Blob} file
 * @param {{offset?:number}} [opts]  byte at which the RIFF tree starts. Every
 *        offset in the diagnosis (`data.offset`, chunk offsets) is ABSOLUTE in
 *        the file, so repair() can slice without knowing about the prefix.
 */
async function examine(file, opts){
    var base = (opts && opts.offset) || 0;
    if (!file || file.size - base < 12){
        return { kind: 'too_small', fatal: 'too_small', issues: ['too_small'], canRepair: false, base: base };
    }
    var ab = await readHead(file, HEAD_SCAN_LIMIT, base);
    var v = new DataView(ab);
    var master = fourCC(v, 0);
    var wave = fourCC(v, 8);
    if (!MASTERS[master] || wave !== 'WAVE'){
        return { kind: 'not_wav', fatal: 'not_wav', issues: ['not_wav'], canRepair: false, base: base };
    }
    if (master === 'RF64' || master === 'BW64'){
        // RF64/BW64 split the size into a ds64 chunk. Surgeon doesn't rewrite
        // those — it would risk silently corrupting >4 GB recordings. Decline
        // cleanly so the caller falls back to FFmpeg.
        return { kind: 'rf64', fatal: 'rf64_unsupported', issues: ['rf64_unsupported'], canRepair: false, base: base };
    }

    var declaredRiff = v.getUint32(4, true);
    var actualSize = file.size - base;
    var expectedRiff = actualSize - 8;
    var sizeWrong = declaredRiff !== expectedRiff && declaredRiff !== expectedRiff - 1;

    var pos = 12;
    var fmt = null, data = null;
    var unknownChunks = [];
    var truncatedChunkWalk = false;

    while (pos + 8 <= v.byteLength){
        var id = fourCC(v, pos);
        if (!isPrintable4CC(id)){ truncatedChunkWalk = true; break; }
        var size = v.getUint32(pos + 4, true);
        var dataStart = pos + 8;

        if (id === 'fmt '){
            // Clamp size to what we've actually read so parseFmt doesn't reach OOB.
            var fmtClamp = Math.min(size, v.byteLength - dataStart);
            fmt = parseFmt(v, dataStart, fmtClamp);
        } else if (id === 'data'){
            data = { offset: base + dataStart, declaredSize: size };
            // 'data' is the audio payload — surgeon stops here. Anything
            // after is metadata (LIST/INFO, id3 tail, etc) we can drop on rebuild.
            break;
        } else {
            unknownChunks.push({ id: id, offset: base + dataStart, size: size });
        }

        var next = dataStart + size + (size & 1); // pad to even
        if (next <= pos || next > v.byteLength){
            // Walked past the head window or sizeoverflow — stop. If we
            // already have fmt, the missing data is probably outside the
            // scan window; the caller can still NOT repair (we need data
            // offset).
            truncatedChunkWalk = next > v.byteLength;
            break;
        }
        pos = next;
    }

    var issues = [];
    if (base > 0) issues.push('junk_prefix');
    if (sizeWrong) issues.push('riff_size_wrong');
    if (!fmt) issues.push('missing_fmt');
    else if (fmt.fatal) issues.push(fmt.fatal);
    else {
        if (!fmt.channels || fmt.channels === 0) issues.push('zero_channels');
        if (!fmt.sampleRate || fmt.sampleRate === 0) issues.push('zero_sample_rate');
        if (!fmt.bitsPerSample) issues.push('zero_bits_per_sample');
        if (fmt.formatTag === 0xFFFE && fmt.subFormat !== 1 && fmt.subFormat !== 3){
            issues.push('exotic_extensible');
        }
    }
    if (!data){
        issues.push('missing_data');
        if (truncatedChunkWalk) issues.push('chunk_walker_lost');
    } else {
        var actualDataSize = file.size - data.offset;
        data.actualSize = actualDataSize;
        if (data.declaredSize === 0) issues.push('data_size_zero');
        else if (data.declaredSize === 0xFFFFFFFF) issues.push('data_size_sentinel');
        else if (data.declaredSize > actualDataSize + 4) issues.push('data_truncated');
    }

    var canRepair = !!(
        fmt && !fmt.fatal &&
        fmt.channels > 0 && fmt.sampleRate > 0 && fmt.bitsPerSample > 0 &&
        data
    );

    return {
        kind: 'wav',
        fatal: null,
        issues: issues,
        canRepair: canRepair,
        base: base,
        riff: { declaredSize: declaredRiff, expectedSize: expectedRiff, actualFileSize: actualSize },
        fmt: fmt,
        data: data,
        unknownChunks: unknownChunks
    };
}

function writeFourCC(arr, off, str){
    arr[off]   = str.charCodeAt(0);
    arr[off+1] = str.charCodeAt(1);
    arr[off+2] = str.charCodeAt(2);
    arr[off+3] = str.charCodeAt(3);
}

/**
 * Clean RIFF/WAVE header. `extensibleTail` (24 bytes) keeps an exotic
 * EXTENSIBLE sub-format verbatim; otherwise the 16-byte PCM/IEEE fmt is used.
 */
function wavHeader(formatTag, channels, sampleRate, bitsPerSample, dataSize, extensibleTail){
    var bytesPerSample = Math.max(1, Math.ceil(bitsPerSample / 8));
    var blockAlign = channels * bytesPerSample;
    var byteRate = sampleRate * blockAlign;
    var fmtSize = extensibleTail ? 40 : 16;
    var headerSize = 12 + 8 + fmtSize + 8;
    var hdr = new Uint8Array(headerSize);
    var hv = new DataView(hdr.buffer);
    writeFourCC(hdr, 0, 'RIFF');
    hv.setUint32(4, 4 + 8 + fmtSize + 8 + dataSize, true);
    writeFourCC(hdr, 8, 'WAVE');
    writeFourCC(hdr, 12, 'fmt ');
    hv.setUint32(16, fmtSize, true);
    hv.setUint16(20, formatTag, true);
    hv.setUint16(22, channels, true);
    hv.setUint32(24, sampleRate, true);
    hv.setUint32(28, byteRate, true);
    hv.setUint16(32, blockAlign, true);
    hv.setUint16(34, bitsPerSample, true);
    if (extensibleTail) hdr.set(extensibleTail, 36);
    writeFourCC(hdr, 20 + fmtSize, 'data');
    hv.setUint32(24 + fmtSize, dataSize, true);
    return hdr;
}

async function repair(file, opts){
    opts = opts || {};
    var diag = opts.diagnosis || (await examine(file, { offset: opts.offset || 0 }));
    if (diag.fatal){
        return { ok: false, reason: diag.fatal, diagnosis: diag };
    }
    if (!diag.canRepair){
        return { ok: false, reason: 'unrepairable', diagnosis: diag };
    }

    var fmt = diag.fmt;
    var data = diag.data;
    var fixes = [];

    var actualDataSize = file.size - data.offset;
    var bytesPerSample = Math.max(1, Math.ceil(fmt.bitsPerSample / 8));
    var blockAlign = fmt.channels * bytesPerSample;
    // Round down to block-aligned boundary so we don't emit a fractional sample
    // at the tail (decoders interpret that as a glitch / pop).
    var correctedDataSize = blockAlign > 0
        ? actualDataSize - (actualDataSize % blockAlign)
        : actualDataSize;

    if (diag.base > 0) fixes.push('junk_prefix_removed');
    if (data.declaredSize !== correctedDataSize) fixes.push('data_size_corrected');
    if (diag.issues.indexOf('riff_size_wrong') > -1) fixes.push('riff_size_corrected');

    // EXTENSIBLE → standard PCM/IEEE_FLOAT downgrade for max decoder compat.
    // Some old players (and Web Audio in older Safari) reject 0xFFFE even when
    // the SubFormat GUID is plain PCM. Keeping the data bytes identical is
    // what makes this lossless.
    var outFormatTag = fmt.formatTag;
    var keepExtensibleBytes = null;
    if (fmt.formatTag === 0xFFFE){
        if (fmt.subFormat === 1){
            outFormatTag = 1; fixes.push('extensible_to_pcm');
        } else if (fmt.subFormat === 3){
            outFormatTag = 3; fixes.push('extensible_to_float');
        } else {
            // Unknown subformat (e.g. ALAW=6, MULAW=7, ADPCM, ATRAC). Preserve
            // the original 24-byte EXTENSIBLE tail — re-flattening could change
            // semantics. Caller can route to FFmpeg if this doesn't decode.
            keepExtensibleBytes = fmt.extensibleBytes;
        }
    }

    var hdr = wavHeader(outFormatTag, fmt.channels, fmt.sampleRate, fmt.bitsPerSample, correctedDataSize, keepExtensibleBytes);
    var byteRate = fmt.sampleRate * blockAlign;

    // file.slice is lazy — no copy until the Blob is read. This keeps memory
    // usage flat for 1+ GB recordings.
    var dataSlice = file.slice(data.offset, data.offset + correctedDataSize);
    var outBlob = new Blob([hdr, dataSlice], { type: 'audio/wav' });

    return {
        ok: true,
        blob: outBlob,
        ext: 'wav',
        fixes: fixes,
        diagnosis: diag,
        fmtOut: {
            formatTag: outFormatTag,
            channels: fmt.channels,
            sampleRate: fmt.sampleRate,
            bitsPerSample: fmt.bitsPerSample,
            dataSize: correctedDataSize,
            outputSize: outBlob.size,
            durationSec: byteRate > 0 ? correctedDataSize / byteRate : 0
        }
    };
}

/**
 * Wrap headerless PCM in a clean container — synchronous, zero-copy.
 *
 * @param {File|Blob} file
 * @param {{offset?:number, shift?:number, fmt:string, channels:number, sampleRate:number}} layout
 *        `fmt` is a PcmLayoutGuess id: u8 | s16le | s16be | s24le | s24be |
 *        s32le | s32be | f32le | f32be. Payload starts at offset + shift.
 * @returns {{ok:boolean, blob?:Blob, ext?:'wav'|'aiff'|'aifc', fixes:string[], fmtOut?:object, reason?:string}}
 */
function buildFromRaw(file, layout){
    var m = /^(u8|s16|s24|s32|f32)(le|be)?$/.exec(layout && layout.fmt || '');
    var channels = layout && layout.channels | 0, rate = layout && layout.sampleRate | 0;
    if (!m || channels < 1 || channels > 64 || rate < 8000 || rate > 384000) return { ok: false, reason: 'bad_layout', fixes: [] };
    var isFloat = m[1] === 'f32';
    var bits = m[1] === 'u8' ? 8 : parseInt(m[1].slice(1), 10);
    var bigEndian = m[2] === 'be';
    var bytesPerSample = bits / 8, blockAlign = channels * bytesPerSample;
    var start = (layout.offset || 0) + (layout.shift || 0);
    var payload = Math.max(0, file.size - start);
    var frames = Math.floor(payload / blockAlign);
    var dataSize = frames * blockAlign;
    if (frames < 1) return { ok: false, reason: 'no_payload', fixes: [] };

    if (bigEndian) return { ok: false, reason: 'big_endian', fixes: [] };
    var hdr = wavHeader(isFloat ? 3 : 1, channels, rate, bits, dataSize, null);
    var blob = new Blob([hdr, file.slice(start, start + dataSize)], { type: 'audio/wav' });
    return {
        ok: true, blob: blob, ext: 'wav',
        fixes: ['header_rebuilt'],
        fmtOut: {
            formatTag: isFloat ? 3 : 1, channels: channels, sampleRate: rate, bitsPerSample: bits,
            bigEndian: bigEndian, dataSize: dataSize, outputSize: blob.size, durationSec: frames / rate
        }
    };
}

window.WavSurgeon = {
    __isStub: false,
    version: '1.1.0',
    isWav: isWav,
    examine: examine,
    repair: repair,
    buildFromRaw: buildFromRaw
};

// Test hook for Playwright — exposes parseFmt + writeFourCC for header round-trips.
// Only present when ?testHook=1 is set, so we don't ship surface area to prod.
if (typeof location !== 'undefined' && /[?&]testHook=1/.test(location.search)){
    window.WavSurgeon._test = { parseFmt: parseFmt, writeFourCC: writeFourCC, HEAD_SCAN_LIMIT: HEAD_SCAN_LIMIT, wavHeader: wavHeader };
}

})();
